import { jwtDecode } from 'jwt-decode';

import { permissionMatches, ROLE_PERMISSIONS } from '@/lib/permissions';
import type { AppPermission } from '@/types/app-permissions';
import type { AppRole } from '@/types/app-roles';

import { useSupabase } from './useSupabase';

interface JwtClaims {
  sub: string;
  user_roles?: AppRole[];
  /**
   * Permisos otorgados individualmente al usuario, sumados a los del rol.
   * Poblados por `public.custom_access_token_hook` v2 desde
   * `core.user_permission_overrides`. Ver .claude/SEGURIDAD_PERMISOS_RBAC_v1.md.
   */
  user_extra_permissions?: AppPermission[];
}

interface ParsedClaims {
  roles: AppRole[];
  extraPerms: AppPermission[];
}

function parseClaims(accessToken: string | undefined): ParsedClaims {
  if (!accessToken) return { roles: [], extraPerms: [] };
  try {
    const payload = jwtDecode<JwtClaims>(accessToken);
    return {
      roles: payload.user_roles ?? [],
      extraPerms: payload.user_extra_permissions ?? [],
    };
  } catch {
    return { roles: [], extraPerms: [] };
  }
}

function roleHasPermission(role: AppRole, perm: AppPermission): boolean {
  if (role === 'superadmin') return true;
  const grants = ROLE_PERMISSIONS[role];
  if (!grants) return false;
  return grants.some((g) => permissionMatches(g, perm));
}

/**
 * True si alguno de los permisos extra otorgados al user cubre el solicitado.
 * Los overrides son matchings exactos en la lista (sin implicación de scopes
 * más allá de `.all`, igual que en roleHasPermission).
 */
function extraPermsCover(
  extraPerms: AppPermission[],
  requested: AppPermission,
): boolean {
  return extraPerms.some((g) => permissionMatches(g, requested));
}

/**
 * Versión simple: chequea un permiso puntual.
 *
 * Lee `user_roles` y `user_extra_permissions` del JWT (poblados por
 * `public.custom_access_token_hook` v2) y resuelve contra
 * `ROLE_PERMISSIONS` + los overrides individuales. Superadmin tiene bypass
 * implícito.
 */
export function usePermission(permission: AppPermission): boolean {
  const { session } = useSupabase();
  if (!session) return false;

  const { roles, extraPerms } = parseClaims(session.access_token);

  if (roles.includes('superadmin')) return true;
  if (extraPermsCover(extraPerms, permission)) return true;
  return roles.some((role) => roleHasPermission(role, permission));
}

interface UsePermissionsResult {
  roles: AppRole[];
  extraPerms: AppPermission[];
  isSuperadmin: boolean;
  has: (perm: AppPermission) => boolean;
  hasAny: (perms: AppPermission[]) => boolean;
  hasAll: (perms: AppPermission[]) => boolean;
}

/**
 * Versión completa: retorna roles + helpers.
 *
 * Útil cuando un componente necesita resolver varios permisos sin
 * repetir el parseo del JWT. Incluye los overrides individuales del user
 * (claim `user_extra_permissions`).
 */
export function usePermissions(): UsePermissionsResult {
  const { session } = useSupabase();
  const { roles, extraPerms } = parseClaims(session?.access_token);
  const isSuperadmin = roles.includes('superadmin');

  const has = (perm: AppPermission): boolean => {
    if (isSuperadmin) return true;
    if (extraPermsCover(extraPerms, perm)) return true;
    return roles.some((role) => roleHasPermission(role, perm));
  };

  return {
    roles,
    extraPerms,
    isSuperadmin,
    has,
    hasAny: (perms) => perms.some(has),
    hasAll: (perms) => perms.every(has),
  };
}
